import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Saguaro } from "@/components/Saguaro";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  usePageMeta({ title: "Owner Login | MyAZBnB", description: "Owner login for MyAZBnB booking management.", path: "/admin/login", noindex: true });

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <Link to="/" className="flex items-center justify-center gap-2 font-serif text-3xl font-semibold text-foreground mb-8">
          <Saguaro className="h-8 w-8 text-primary" /> The East Valley
        </Link>
        <form onSubmit={submit} data-testid="admin-login-form" className="bg-card rounded-2xl shadow-sm p-8 space-y-5">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-foreground">Owner Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Sign in to manage your rental</p>
          </div>
          {error && <div className="bg-destructive/10 text-destructive text-sm rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-lg" placeholder="owner@cozynest.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-lg" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-primary h-11">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </Button>
        </form>
      </div>
    </div>
  );
}
